import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { invokeWithRetry } from '@/lib/supabaseRetry';
import * as Sentry from '@sentry/react';

// Helper function to calculate glycemic range from score (multiply by 100 first)
const calculateGlycemicRange = (score: number): string => {
  const scaledScore = score * 100;
  if (scaledScore <= 54) return 'Low';
  if (scaledScore <= 70) return 'Moderate';
  return 'High';
};

export interface MealImpact {
  user_experiment_id: string;
  user_id: string;
  meal_id: string;
  food_log_names: string[];
  food_log_barcodes: string[];
  meal_timestamp_utc: string;
  timezone: number;
  adjusted_meal_timestamp_utc: string;
  experiment_timestamp_local: string;
  image_links: string[];
  start_glucose_level: number;
  peak_glucose_level: number;
  time_to_peak: number;
  time_to_return: string;
  iauc: number;
  indicative_glycemic: number;
  glycemic_range: string;
  experiment_valid: boolean;
  satiety_score: number;
  branded_food_fdc_id: number;
  meal_impact_updated_at: string;
  glucose_samples: number[][];
  baseline_glucose_samples: number[][];
}

export interface ExperimentResults {
  experiment_id: string;
  meal_impacts: MealImpact[];
  total_count: number;
}

export const useExperimentResults = (experimentId: string) => {
  return useQuery({
    queryKey: ['experiment-results', experimentId],
    queryFn: async (): Promise<ExperimentResults> => {
      console.log('useExperimentResults: Fetching results for experiment:', experimentId);

      try {
        const { data: apiResponse, error } = await invokeWithRetry('tastermonial-api', {
          body: {
            method: 'GET',
            endpoint: `/api/v2/experiments/${experimentId}/results`,
          },
        });

        if (error) {
          Sentry.captureException(error);
          throw new Error(error.message || 'Failed to fetch experiment results');
        }

        if (apiResponse.status !== 200) {
          throw new Error(`API error: ${apiResponse.statusText || 'Failed to fetch experiment results'}`);
        }

        const resultsData = apiResponse.data as ExperimentResults;
        console.log('useExperimentResults: Results data:', resultsData);

        // Calculate local timestamps and update glycemic ranges for each meal impact
        const processedResults: ExperimentResults = {
          ...resultsData,
          meal_impacts: resultsData.meal_impacts.map(impact => {
            const utcDate = new Date(impact.meal_timestamp_utc);
            const localDate = new Date(utcDate.getTime() + (impact.timezone * 60 * 60 * 1000));
            return {
              ...impact,
              experiment_timestamp_local: localDate.toISOString(),
              glycemic_range: calculateGlycemicRange(impact.indicative_glycemic)
            };
          })
        };

        return processedResults;
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    enabled: !!experimentId,
  });
};